/**
 * @module employee.actions
 */
import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import { getBooleanFlagValue } from '@worktango/feature-flags-react';
import {
  applyMappingsAndTransformations,
  transformationSourceColumns,
  type ColumnMapping,
  type Transformation,
} from '@worktango/employee-import-pipeline';
import { validateImport as validateImportPackage, type EmployeeImportRow } from '@worktango/employee-import-validation';
import { normalize } from 'normalizr';
import { graphqlRequest } from '~/adapters/graphqlRequest';
import { httpRequest } from '~/adapters/httpRequest';
import { convert } from '~/tools/convert';
import { list } from '~/tools/list';
import { datetime } from '~/tools/datetime';
import { validate } from '~/tools/validate';
import { text } from '~/tools/text';
import { keys as attributeTypeKeys } from '~/config/settings/attribute_types.settings';
import { importTypes } from '~/config/settings/employee.settings';
import { statuses as notificationStatuses } from '~/config/settings/notification.settings';
import { host } from '~/config/settings/worktango.settings';
import { employeeAttributeActions } from './employee_attribute.actions';
import * as l10n from '../languages';
import * as transformers from '../transformers';
import * as schemas from '../schemas';
import {
  CreateEmployeeParams,
  EmployeeAttributeObject,
  EmployeeDict,
  ExportEmployeesResponse,
  FetchEmployeeOptions,
  FetchEmployeesOptions,
  NormalizedEmployees,
  PageEmployeeOptions,
  ModifyEmployeeParams,
  LanguageDict,
  ImportEmployeesValidationResponse,
  EmployeeAttribute,
} from '~/interfaces/employee.interface';
import type { AppDispatch, RootState } from '../store';
import {
  CreateEmployeeDocument,
  EmployeeImportCoreField,
  EmployeeImportMode,
  FetchCompanyEmployeeIdsDocument,
  FetchCompanyEmployeesAndUsersDocument,
  FetchEmployeeImportColumnMappingsDocument,
  FetchEmployeeImportTransformationsDocument,
  FetchEmployeesDocument,
  FetchEmployeesWithoutAttributesDocument,
  ImportEmployeesDocument,
  NotifyPrivateAttributeExportDocument,
  ScheduleEmployeeImportDocument,
  UpdateCompanyEmployeeIdsDocument,
  UpdateEmployeeDocument,
} from '~/gql/graphql';
import * as excel from '~/adapters/excel';
import { coalesce } from '~/helpers/coalesce';

// The backend throws this exact prefix when one or more `isUnique` attribute
// values collide with an existing employee (see `editEmployeeAttributesWithValues`
// in the API's `employee-repository`). The remainder of the message is a
// comma-separated list of attribute IDs.
const UNIQUE_ATTRIBUTE_ERROR_PREFIX = 'Duplicated values for following unique attribute Ids: ';

const buildUniqueAttributeErrorNotification = (
  errorMessage: string,
  employeeAttributes: { [id: string]: { name?: string } },
) => {
  if (typeof errorMessage !== 'string' || !errorMessage.startsWith(UNIQUE_ATTRIBUTE_ERROR_PREFIX)) {
    return null;
  }

  const names = errorMessage
    .slice(UNIQUE_ATTRIBUTE_ERROR_PREFIX.length)
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
    .map((id) => employeeAttributes[id]?.name ?? id);

  if (names.length === 0) return null;

  return {
    title: l10n.t('form.invalid'),
    message: `${l10n.t('form.invalidAttribute', { name: names.join(', ') })}. ${l10n.t('form.isUnique')}.`,
    status: notificationStatuses.error,
  };
};

const fetchEmployeeEntities = async (
  companyId: string,
  /* istanbul ignore start -- defaults are never used; all callers pass explicit args */
  includeAttributeValues: boolean = false,
  languages = {},
  options: FetchEmployeeOptions = {} as FetchEmployeeOptions,
  /* istanbul ignore stop */
) => {
  const {
    surveyTypeId,
    search,
    batchSearch,
    attributeValues,
    employeeIds,
    offset,
    limit,
    // order, TODO: implement sorting logic once backend supports it
    // orderBy,
    filterBy: { isActive },
  } = options;
  const formattedAttributeValues = attributeValues
    ? transformers.attributeValuesToGraphqlFormat(attributeValues)
    : null;
  const queryDocument = includeAttributeValues ? FetchEmployeesDocument : FetchEmployeesWithoutAttributesDocument;

  const { data: res } = await graphqlRequest<any>(queryDocument, {
    companyId,
    surveyTypeId,
    search,
    batchSearch,
    attributes: formattedAttributeValues,
    employeeIds,
    limit,
    offset,
    enabled: isActive,
  });

  // @ts-ignore
  if (res?.error) throw new Error('Unable to fetch employees', res.error);

  const rawEmployees = res?.employeeDetails?.results;
  const employeeCount = res?.employeeDetails?.count;

  // WARNING: Add language ISO-code to each employee as attribute `lang`. If
  // unable to find a matching ISO-code in `languages` for `employee.languageId`
  // the app's `defaultLang` will be used instead. (from the transformer).
  //
  // OPTIMIZE: Update server so `lang` ISO-code is returned by default to avoid
  // this transformation for better performance.
  const rawEmployeesWithLang = rawEmployees.map((employee) => ({
    ...employee,
    lang: languages[employee.languageId]?.code,
  }));

  const employees = transformers.employeesFrom(rawEmployeesWithLang);
  const normalizedData = normalize(employees, [schemas.employee]);

  return {
    ...normalizedData,
    count: employeeCount,
  };
};

// Fetch a single employee.
// REFACTOR: can this be simplified to share logic with `fetchEmployees()`?
const fetchEmployee = createAsyncThunk<EmployeeDict, { companyId?: string; employeeId?: number }, { state: RootState }>(
  'employee/fetchEmployee',
  async ({ companyId, employeeId }, { rejectWithValue, getState }) => {
    if (!companyId) return rejectWithValue('`companyId` is required');

    const { data: res } = await graphqlRequest<any>(FetchEmployeesDocument, {
      companyId,
      employeeIds: [employeeId],
      limit: 1,
    });

    if (res?.error) return rejectWithValue(res.error);

    const rawEmployee = res?.employeeDetails?.results[0];

    if (!rawEmployee)
      return rejectWithValue(`no employee record for employeeId ${employeeId} and companyId ${companyId}`);

    const languages = getState().entities.languages;
    const employee = transformers.employeeFrom({
      ...rawEmployee,
      lang: languages[rawEmployee.languageId]?.code,
    });
    const normalizedData = normalize(employee, schemas.employee);

    return normalizedData;
  },
);

// Fetch multiple employees by page.
const FETCH_ALL_PAGE_SIZE = 10000;

const fetchEmployees = createAsyncThunk<NormalizedEmployees, FetchEmployeesOptions, { state: RootState }>(
  'employee/fetchEmployees',
  async (
    {
      companyId,
      surveyTypeId,
      search,
      batchSearch,
      limit,
      offset,
      attributeValues,
      employeeIds,
      isActive,
      includeAttributes = true,
      fetchAll = false,
    } = {},
    { rejectWithValue, getState },
  ) => {
    if (!companyId) return rejectWithValue('`companyId` is required');

    const account = getState().account;
    const languages = getState().entities.languages;

    const baseOptions = {
      surveyTypeId,
      search,
      batchSearch,
      attributeValues,
      employeeIds,
      filterBy: {
        // @ts-ignore @TODO Epic WT-21600
        isActive,
      },
    };

    try {
      if (!fetchAll) {
        // @ts-ignore @TODO Epic WT-21600
        return await fetchEmployeeEntities(account.companyId, includeAttributes, languages, {
          ...baseOptions,
          offset,
          limit,
        });
      }

      // Paginate through all employees, merging each page into a combined result.
      const combinedEmployees: EmployeeDict = {};
      const combinedResult: number[] = [];
      let currentOffset = 0;
      let total = Infinity;

      while (currentOffset < total) {
        // @ts-ignore @TODO Epic WT-21600
        const page = await fetchEmployeeEntities(account.companyId, includeAttributes, languages, {
          ...baseOptions,
          offset: currentOffset,
          limit: FETCH_ALL_PAGE_SIZE,
        });

        if (currentOffset === 0) total = page.count ?? 0;
        Object.assign(combinedEmployees, page.entities?.employees ?? {});
        combinedResult.push(...(page.result as number[]));
        currentOffset += FETCH_ALL_PAGE_SIZE;
      }

      return { entities: { employees: combinedEmployees }, result: combinedResult, count: total };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// TODO: Server doesn't currently support ordering/sorting so for now, simply
// preserving whatever order backend has is using (assuming it is by id?)
const pageEmployees = createAsyncThunk<EmployeeDict, PageEmployeeOptions, { state: RootState }>(
  'employee/pageEmployees',
  async ({ attributeValues, filterBy, orderBy, order, page, per, search } = {}, { rejectWithValue, getState }) => {
    const account = getState().account;
    const employeeUI = getState().ui.employees;
    const languages = getState().entities.languages;

    // Handle fallback values: use provided value, else use value already chosen
    // in UI state, else use default or empty value.
    const activeSearch = coalesce(coalesce(search, employeeUI.search), '');
    const activeFilterBy = coalesce(coalesce(filterBy, employeeUI.filterBy), { isActive: true });
    const activeOrderBy = coalesce(coalesce(orderBy, employeeUI.orderBy), 'name');
    const activeOrder = coalesce(coalesce(order, employeeUI.order), 'asc');
    const activeAttributeValues = coalesce(coalesce(attributeValues, employeeUI.attributeValues), {});
    const activePage = coalesce(coalesce(page, employeeUI.page), 0);
    const limit = coalesce(coalesce(per, employeeUI.per), 15);
    const offset = activePage * limit;
    const isActive = activeFilterBy.isActive === 'true' || activeFilterBy.isActive === true;

    try {
      // @ts-ignore @TODO Epic WT-21600
      const employeeData = await fetchEmployeeEntities(account.companyId, true, languages, {
        search: activeSearch,
        attributeValues: activeAttributeValues,
        offset,
        limit,
        filterBy: {
          isActive,
        },
      });
      const employeeIds = employeeData?.entities?.employees
        ? Object.keys(employeeData.entities.employees).map(Number)
        : [];

      return {
        ...employeeData,
        pageEmployeeIds: employeeIds,
        pagination: {
          search: activeSearch,
          attributeValues: activeAttributeValues,
          filterBy: activeFilterBy,
          orderBy: activeOrderBy,
          order: activeOrder,
          page: activePage,
          per: limit,
          count: employeeData?.count,
        },
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const createEmployee = createAsyncThunk<EmployeeDict, CreateEmployeeParams, { state: RootState }>(
  'employee/createEmployee',
  async ({ companyId, isActive, lang, employeeAttributeValues }, { rejectWithValue, getState }) => {
    const state = getState();
    const employeeAttributes = state.entities.employeeAttributes;
    const languages = state.entities.languages;

    const enabled = Boolean(isActive);
    const languageId = Number(languages?.codeIds[lang]);
    const attributesWithValues = Object.keys(employeeAttributeValues).map((attrId) => ({
      id: Number(attrId),
      action: 'CREATE',
      type: employeeAttributes[attrId].type,
      value: employeeAttributeValues[attrId],
    }));

    try {
      const { data: res } = await graphqlRequest<any>(CreateEmployeeDocument, {
        companyId,
        employee: {
          enabled,
          languageId,
          attributesWithValues,
        },
      });

      const rawNewEmployee = res?.addEmployee;
      rawNewEmployee.lang = languages.codeIds[rawNewEmployee.languageId];

      const employee = transformers.updatedEmployeeFrom(rawNewEmployee);
      const normalizedData = normalize(employee, schemas.employee);

      return {
        ...normalizedData,
        notification: {
          title: l10n.t('notifications.title.done'),
          message: l10n.t('notifications.message.employeeCreated'),
          status: notificationStatuses.success,
        },
      };
    } catch (error) {
      const errorMessage = coalesce((error as { message?: string })?.message, '');
      const notification = coalesce(buildUniqueAttributeErrorNotification(errorMessage, employeeAttributes), {
        title: l10n.t('form.invalid'),
        message: errorMessage || l10n.t('error.somethingWentWrong'),
        status: notificationStatuses.error,
      });

      return rejectWithValue({ notification });
    }
  },
);

const modifyEmployee = createAsyncThunk<EmployeeDict, ModifyEmployeeParams, { state: RootState }>(
  'employee/modifyEmployee',
  async ({ employeeId, companyId, isActive, lang, employeeAttributeValues }, { rejectWithValue, getState }) => {
    const state = getState();
    const employeeAttributes = state.entities.employeeAttributes;
    const languages = state.entities.languages;

    const enabled = Boolean(isActive);
    const languageId = Number(languages?.codeIds[lang]);

    const attributesWithValues = Object.keys(employeeAttributeValues).map((attrId) => {
      const id = Number(attrId);
      const type = employeeAttributes[attrId].type;
      const value = employeeAttributeValues[attrId];
      const action = value === undefined || value === null || value === '' ? 'DELETE' : 'UPDATE';

      return {
        id,
        action,
        type,
        value,
      };
    });
    try {
      const { data: res } = await graphqlRequest<any>(UpdateEmployeeDocument, {
        employeeId,
        companyId,
        employee: {
          enabled,
          languageId,
          attributesWithValues,
        },
      });

      if (res?.error) return rejectWithValue(res.error);

      const rawUpdatedEmployee = res?.updateEmployee;
      rawUpdatedEmployee.lang = coalesce(languages[rawUpdatedEmployee.languageId]?.code, 'en-ca');
      const employee = transformers.updatedEmployeeFrom(rawUpdatedEmployee);
      const normalizedData = normalize(employee, schemas.employee);
      return {
        ...normalizedData,
        notification: {
          title: l10n.t('notifications.title.done'),
          message: l10n.t('notifications.message.employeeUpdates'),
          status: notificationStatuses.success,
        },
      };
    } catch (error) {
      const errorMessage = coalesce((error as { message?: string })?.message, '');
      const notification = coalesce(buildUniqueAttributeErrorNotification(errorMessage, employeeAttributes), {
        title: l10n.t('form.invalid'),
        message: errorMessage || l10n.t('error.somethingWentWrong'),
        status: notificationStatuses.error,
      });

      return rejectWithValue({ notification });
    }
  },
);

const removeEmployee = createAsyncThunk(
  'employee/removeEmployee',
  async ({ employeeId, companyId }: { employeeId: number; companyId: number }) => {
    return {};
  },
);

/**
 * Make a network request for employee data, based on provided options, and
 * convert the received data into an Excel file and trigger a download in
 * the browser.
 */
const exportEmployees = createAsyncThunk<
  ExportEmployeesResponse,
  {
    includePrivateAttributes?: boolean;
    includeDatabaseEmployeeId?: boolean;
    includeArchivedAttributes?: boolean;
  },
  { state: RootState; dispatch: AppDispatch }
>(
  'employee/exportEmployees',
  async (
    { includePrivateAttributes = false, includeDatabaseEmployeeId = false, includeArchivedAttributes = false },
    { getState, dispatch, rejectWithValue },
  ) => {
    const ACTIVE_KEY = 'Enabled';
    const LANGUAGE_KEY = 'Language';
    const DB_EMPLOYEE_ID_KEY = 'LegacyPulseEmployeeId';

    const account = getState().account;

    // @ts-ignore @TODO Epic WT-21600
    const { codeIds, ...languages }: { codeIds: string[]; languages: LanguageDict } = getState().entities.languages;
    const {
      search,
      attributeValues,
      filterBy: { isActive },
    } = getState().ui.employees;

    let employeeAttributes: EmployeeAttributeObject[] = Object.values(getState().entities.employeeAttributes);

    // If no employeeAttributes are currently present in the state, make a new
    // network request to retreive them (this will also store the latest data in the state).
    if (employeeAttributes.length === 0) {
      const action = await dispatch(
        // @ts-ignore @TODO Epic WT-21600
        employeeAttributeActions.fetchEmployeeAttributes({
          // @ts-ignore @TODO Epic WT-21600
          companyId: account.companyId,
          includePrivateAttributes,
        }),
      );

      employeeAttributes = Object.values(action.payload.entities.employeeAttributes);
    }

    if (!includePrivateAttributes) {
      employeeAttributes = employeeAttributes.filter((attr) => !attr.isPrivate);
    }

    if (!includeArchivedAttributes) {
      // @ts-ignore @TODO Epic WT-21600
      employeeAttributes = employeeAttributes.filter((attr) => !attr.isArchived);
    }

    // Sort attributes first by alphabetical name, then group all "system"
    // attributes at the beginning of the list.
    employeeAttributes.sort(list.byAttribute('name', 'asc'));
    employeeAttributes.sort(list.byAttribute('baseAttributeId', 'asc'));

    try {
      if (includePrivateAttributes) {
        await graphqlRequest<any>(NotifyPrivateAttributeExportDocument, { companyId: account.companyId });
      }

      // @ts-ignore @TODO Epic WT-21600
      const employeeData = await fetchEmployeeEntities(account.companyId, true, languages, {
        filterBy: {
          isActive,
        },
        search,
        attributeValues,
      });

      // @ts-ignore @TODO Epic WT-21600
      const employees = employeeData && Object.values(employeeData?.entities?.employees);

      const status = isActive ? l10n.t('system.field.enabled') : l10n.t('system.field.disabled');

      const filenameSuffix = includePrivateAttributes ? '-with-private-attributes' : '';
      const filename = `Employees-${status}${filenameSuffix}`;

      const KW_EXPECTED_COLUMN_NAME = 'role';
      const KW_EXPECTED_ROLE_NAME = 'SURVEY_CUSTOM';

      const columnHeaders = [
        ...(includeDatabaseEmployeeId ? [DB_EMPLOYEE_ID_KEY] : []),
        ACTIVE_KEY,
        ...employeeAttributes.map((attr) => {
          const name = coalesce(attr.translations[account.lang].name, attr.name);
          return text.toTitlecase(name);
        }),
        LANGUAGE_KEY,
      ];
      /* istanbul ignore start -- workspace-only column (kazoo-web embedded mode) */
      if (account.workspace) {
        columnHeaders.push(KW_EXPECTED_COLUMN_NAME);
      }
      /* istanbul ignore stop */

      const employeesTab = {
        name: l10n.t('admin.employees.header'),
        titles: columnHeaders,
        rows: employees.map((employee) => {
          const columns = [
            ...(includeDatabaseEmployeeId ? [employee.id] : []),
            employee.isActive,
            ...employeeAttributes.map((attr) => {
              let value = employee.employeeAttributeValues[attr.id];

              if (value && attr.type === 'DATE') {
                const year = Number(value.substring(0, 4));
                const month = Number(value.substring(5, 7)) - 1;
                const day = Number(value.substring(8, 10));
                const date = new Date(year, month, day);
                value = datetime.format(date, 'yyyy-MM-dd');
              }
              return value;
            }),
            employee.lang,
          ];
          /* istanbul ignore start -- workspace-only role column (kazoo-web embedded mode) */
          if (account.workspace && employee.isUser) columns.push(KW_EXPECTED_ROLE_NAME);
          /* istanbul ignore stop */
          return columns;
        }),
      };

      const languagesTab = {
        name: l10n.t('languageCode'),
        titles: [l10n.t('system.field.language'), l10n.t('languageCode')],
        rows: [
          ...Object.values(languages).map((language) => [l10n.t(language.code), language.code]),
          [], // NOTE: appending an empty row to separate the description below from the regular data ;)
          [l10n.t('language.code.desc')],
        ],
      };

      // Will trigger browser download.
      excel.toFile(filename, [employeesTab, languagesTab]);

      return {
        filename,
        notification: {
          title: l10n.t('notifications.title.done'),
          message: l10n.t('employees.export.success', { filename: `${filename}.xlsx` }),
          status: notificationStatuses.success,
        },
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const generateSampleEmployeeImport = createAction(
  'employee/generateSampleEmployeeImport',
  ({ employeeAttributes, supportedLanguages, lang }) => {
    // TODO: handle this in a more maintainable way.
    const ACTIVE_KEY = 'Enabled';
    const LANGUAGE_KEY = 'Language';

    const filename = 'Employees-Sample'; // TODO: maybe create a dynamic name that is more descriptive?

    const employeesTab = {
      name: l10n.t('admin.employees.header'),
      titles: [
        ACTIVE_KEY,
        ...employeeAttributes.map((attr) => {
          const name = attr.translations[lang].name || attr.name;

          return text.toTitlecase(name);
        }),
      ],
      rows: [
        [
          true, // `employee.isActive` (i.e., "enabled")
          ...employeeAttributes.map((attr) => convert.attributeTypeToSampleValue(attr.type)),
        ],
      ],
    };

    const languagesTab = {
      name: l10n.t('languageCode'),
      titles: [LANGUAGE_KEY, l10n.t('languageCode')],
      rows: [
        ...supportedLanguages.map((supportedLang) => [l10n.t(supportedLang), supportedLang]),
        [], // NOTE: appending an empty row to separate the description below from the regular data ;)
        [l10n.t('language.code.desc')],
      ],
    };

    // Will trigger browser download.
    excel.toFile(filename, [employeesTab, languagesTab]);

    return {
      payload: {
        filename,
      },
    };
  },
);

const getValidRoleNames = async (companyId: number) => {
  const rolesURL = `${host}/user-roles?companyId=${companyId}`;
  const response = await httpRequest<any>(rolesURL);
  const roles = response.payload.roles;

  return roles.reduce((acc, current) => {
    const languages = Object.keys(current.translations);
    const names = languages.map((lang) => current.translations[lang].name.toLowerCase());
    acc.push(...names);
    return acc;
  }, []);
};

const CORE_FIELD_FROM_ENUM: Record<EmployeeImportCoreField, 'employeeId' | 'enabled' | 'languageId' | 'role'> = {
  [EmployeeImportCoreField.EmployeeId]: 'employeeId',
  [EmployeeImportCoreField.Enabled]: 'enabled',
  [EmployeeImportCoreField.LanguageId]: 'languageId',
  [EmployeeImportCoreField.Role]: 'role',
};

/**
 * Fetches the requesting user's company's configured header-to-field mappings (WT-31243) and
 * translates each into the shape `applyColumnMappings` expects. The GraphQL enum values
 * (`EMPLOYEE_ID`, etc.) are translated back to the lowerCamel strings `EmployeeImportInput` and
 * the pipeline package both use. A company with none configured gets an empty array, which
 * `applyColumnMappings` already treats as a no-op.
 */
async function fetchColumnMappings(): Promise<ColumnMapping[]> {
  const { data: res } = await graphqlRequest<any>(FetchEmployeeImportColumnMappingsDocument, {});
  const mappings = coalesce(res?.employeeImportColumnMappings, []);

  return mappings.map((mapping: any): ColumnMapping => {
    if (mapping.excluded) {
      return { sourceColumnName: mapping.sourceColumnName, excluded: true };
    }
    return mapping.targetCoreField
      ? {
          sourceColumnName: mapping.sourceColumnName,
          targetCoreField: CORE_FIELD_FROM_ENUM[mapping.targetCoreField as EmployeeImportCoreField],
        }
      : {
          sourceColumnName: mapping.sourceColumnName,
          targetAttributeId: mapping.targetAttributeId,
          targetAttributeName: mapping.targetAttributeName,
        };
  });
}

/**
 * Fetches the requesting user's company's configured custom transformations (WT-31257). The
 * GraphQL enum value for `transformType` (e.g. `CONCATENATE`) is already identical to the string
 * `applyTransformation` switches on, so it's passed through as-is -- only `targetCoreField` needs
 * translating back to the lowerCamel string, same as `fetchColumnMappings`.
 */
async function fetchTransformations(): Promise<Transformation[]> {
  const { data: res } = await graphqlRequest<any>(FetchEmployeeImportTransformationsDocument, {});
  const transformations = coalesce(res?.employeeImportTransformations, []);

  return transformations.map(
    (transformation: any): Transformation =>
      ({
        transformType: transformation.transformType,
        definition: transformation.definition,
        ...(transformation.targetCoreField
          ? { targetCoreField: CORE_FIELD_FROM_ENUM[transformation.targetCoreField as EmployeeImportCoreField] }
          : {
              targetAttributeId: transformation.targetAttributeId,
              targetAttributeName: transformation.targetAttributeName,
            }),
      }) as Transformation,
  );
}

const validateEmployeesImportData = createAsyncThunk<
  { payload: { isValid: boolean; errorMsg?: string } } | unknown,
  {
    buffer: ArrayBuffer;
    employeeAttributes: EmployeeAttribute[];
    supportedLanguages: string[];
    companyId: number;
  }
>(
  'employee/validateEmployeesImportData',
  // @ts-ignore @TODO Epic WT-21600
  async ({ buffer, employeeAttributes, supportedLanguages, companyId, importType }, { rejectWithValue }) => {
    const validRoleNames = await getValidRoleNames(companyId);
    const columnMappings = await fetchColumnMappings();
    const transformations = await fetchTransformations();

    const paddingIndex = 2; // Header + indexes starting with 0
    const employees: EmployeeImportRow[] = excel
      .employeesToJson(buffer, { dateFormat: 'YYYY-MM-DD' })
      .map((employee, index) => ({ rowIndex: index + paddingIndex, employee }))
      // Blank rows are dropped against the raw spreadsheet data, before mapping or
      // transformations run -- a FIXED_VALUE (or a CONCATENATE with a non-empty default) would
      // otherwise turn a genuinely blank row non-empty and let it survive as a phantom employee.
      .filter(({ employee }) => !excel.isRowEmpty(employee))
      .map(({ rowIndex, employee }) => ({
        rowIndex,
        // Column mapping and transformations run before any of the matching below: a header
        // renamed here (e.g. a customer's "EE_ID" -> "employee id") is indistinguishable from an
        // already-correct header by the time validateImport's own matching runs. A company with
        // neither configured sees no behavior change.
        row: applyMappingsAndTransformations(columnMappings, transformations, convert.toLowerKeys(employee)),
      }));

    const { data: existingEmployeesRes } = await graphqlRequest<any>(FetchCompanyEmployeesAndUsersDocument, {
      companyId,
    });
    if (!existingEmployeesRes?.employeeDetails?.results) {
      return rejectWithValue(`Unable to fetch company employee IDs for company ${companyId}`);
    }
    const existingEmployees = coalesce(existingEmployeesRes?.employeeDetails?.results, []);

    // The full rule set (duplicates, modified employee IDs, required/type checks, role and
    // permission conflicts) lives in @worktango/employee-import-validation so a future
    // server-side import path can run the exact same checks -- see the package's own docs for
    // why it returns structured findings instead of pre-localized strings.
    const { isValid, findings, warnings } = validateImportPackage({
      employees,
      // @ts-ignore @TODO Epic WT-21600
      employeeAttributes,
      supportedLanguages,
      existingEmployees,
      validRoleNames,
      additionalRecognizedAttributeNames: transformationSourceColumns(transformations),
      importType,
    });

    // A collection of all individual validation errors detected for detailed
    // feedback to user so they can make corrections.
    const errors: ImportEmployeesValidationResponse = {
      duplicateUniqueValues: findings.duplicateUniqueValues.map((f) => `${f.attrName}: ${f.value}`),
      unknownAttributeNames: findings.unknownAttributeNames.map((f) => f.attrName),
      missingRequiredErrors: findings.missingRequiredErrors.map((f) =>
        l10n.t('employee.import.error.missingField', { employeeId: f.employeeId, attrName: f.attrName }),
      ),
      attributeTypeErrors: findings.attributeTypeErrors.map((f) =>
        l10n.t('employee.import.error.invalidType', {
          employeeId: f.employeeId,
          attrType: f.attrType,
          attrName: f.attrName,
        }),
      ),
      modifiedEmployeeIdErrors: findings.modifiedEmployeeIdErrors.map((f) =>
        l10n.t('employee.import.error.modifiedEmployeeId', {
          employeeName: f.employeeName,
          oldEmployeeId: f.oldEmployeeId,
          newEmployeeId: f.newEmployeeId,
        }),
      ),
      unsupportedLangs: findings.unsupportedLangs.map((f) => f.language),
      unknownRoleNamesRows: findings.unknownRoleNamesRows.map((f) => f.rowIndex),
      customPermissionsRows: findings.customPermissionsRows.map((f) => f.rowIndex),
      userMissingEmailRows: findings.userMissingEmailRows.map((f) => f.rowIndex),
      invitingDisabledEmployee: findings.invitingDisabledEmployee.map((f) => f.rowIndex),
    };

    // Format error messages into an array with mainError and subErrors,
    // The intention is to render a list of errors with sub-items if necessary.
    const errorsArray: { mainError: string; subErrors?: string[] }[] = [];
    Object.keys(errors).forEach((errorKey) => {
      if (!errors[errorKey]?.length) {
        return;
      }

      if (['unknownRoleNamesRows', 'customPermissionsRows', 'userMissingEmailRows'].includes(errorKey)) {
        // Displays the error in one line
        const msg = l10n.t(`employee.import.error.${errorKey}`, { rows: errors[errorKey].join(', ') });
        errorsArray.push({ mainError: msg, subErrors: [] });
      } else {
        // Displays the error in multiple lines
        const msgGroupHeader = l10n.t(`employee.import.error.${errorKey}`);
        errorsArray.push({ mainError: `${msgGroupHeader}:`, subErrors: errors[errorKey] });
      }
    });

    return { isValid, errorsArray, warnings };
  },
);

/**
 * Take an Excel file (specifically a `.xlsx` file format) containing a set of
 * employee-attributes referenced by exact name in the first row of columns
 * followed by one or more rows of employee data values to be imported to the
 * app database.
 *
 * If an employee already exists for a given ID, it will be overwritten.
 *
 * If a parsing error is encountered, the entire process will be stopped before
 * making any changes on the server.
 */
/**
 * Chunked, rather than a single `String.fromCharCode(...bytes)` spread, since that call blows the
 * engine's argument-count limit on a large employee file (tens of thousands of rows easily
 * exceeds it) -- `btoa` itself has no such limit once fed a bounded-length binary string.
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

const importEmployees = createAsyncThunk<
  Notification | unknown,
  {
    importType: string;
    buffer: Buffer;
    fileName?: string;
  },
  { state: RootState; dispatch: AppDispatch }
>('employee/importEmployees', async ({ importType, buffer, fileName }, { getState, dispatch, rejectWithValue }) => {
  const account = getState().account;
  const isUpdate = importType === importTypes.update;

  // Parsing, column mapping, transformations, and validation all move server-side into the async
  // worker once this flag is on -- the client's only job is handing over the raw file exactly as
  // selected, then showing the existing "Import started" confirmation below. See
  // Employees.layout.tsx's handleValidateImport for the matching skip on the validation side.
  if (getBooleanFlagValue('async-employee-import', false)) {
    await graphqlRequest<any>(ScheduleEmployeeImportDocument, {
      companyId: account.companyId,
      fileContents: arrayBufferToBase64(buffer as unknown as ArrayBuffer),
      mode: isUpdate ? EmployeeImportMode.Append : EmployeeImportMode.Overwrite,
      fileName,
    });

    return {
      notification: {
        title: l10n.t('employee.import.scheduled.title'),
        message: l10n.t('employee.import.scheduled.message'),
        status: notificationStatuses.success,
      },
    };
  }

  // TODO: Is there a better way of maintaining these key values?
  // WARNING: The expected key strings are explicitly defined here, but then
  // converted to lower case (along with all employee attribute keys) in order
  // to match without case sensitivity. This is not ideal and should be revised.
  const COMPANY_EMPLOYEE_ID_KEY = 'Employee Id'.toLowerCase();
  const LANGUAGE_KEY = 'Language'.toLowerCase();
  const ENABLED_KEY = 'Enabled'.toLowerCase();
  const ROLE_KEY = '$Survey_Role'.toLowerCase();

  const languages = getState().entities.languages;
  const attributes: EmployeeAttributeObject[] = Object.values(getState().entities.employeeAttributes);
  const columnMappings = await fetchColumnMappings();
  const transformations = await fetchTransformations();

  const employees: { [key: string]: string }[] = excel
    .employeesToJson(buffer, { dateFormat: 'YYYY-MM-DD' })
    // Blank rows are dropped against the raw spreadsheet data, same rationale as
    // validateEmployeesImportData: a transformation with no applicable input for a genuinely
    // blank row must not turn it non-empty and let it survive as a phantom employee.
    .filter((employee) => !excel.isRowEmpty(employee))
    // Column mapping and transformations run before any of the matching below, same insertion
    // point and rationale as validateEmployeesImportData: a company with neither configured sees
    // no behavior change.
    .map((employee) => applyMappingsAndTransformations(columnMappings, transformations, convert.toLowerKeys(employee)));

  //Does a role column exist in the file?
  const hasRoleUpdate = employees.length > 0 && ROLE_KEY in employees[0];

  // Transform import data to format expected by backend (i.e., `EmployeeImportInput` GraphQL Type).
  const importableEmployees = employees.map((employee) => {
    const { [ENABLED_KEY]: isActive, ...employeeAttributeValues } = employee;

    // We only use lower case for all company employee IDs and email
    const companyEmployeeId = employee[COMPANY_EMPLOYEE_ID_KEY]?.toLowerCase();
    const enabled = validate.toBoolean(isActive);
    const languageId = coalesce(languages.codeIds[employeeAttributeValues[LANGUAGE_KEY]], null);

    const importableAttributeValues = attributes.reduce(
      (acc, attr) => {
        // Find first case-insensitive attribute key match.
        // TODO: This is kind of brittle; is there a more precise way of matching attributes?
        const matchingKey = Object.keys(employeeAttributeValues).find((key) => {
          return key === attr.name.toLowerCase();
        });

        if (!matchingKey) return acc;

        // Transform all email and companyEmployeeId values to lowercase to normalize
        // them and prevent name collisions with case sensitive values.
        // WARNING: should ID values be transformed? what if a customer wants to
        // explicitly use upper and lower case IDs like `83dEA-ePp24`?
        const value =
          (attr.type === attributeTypeKeys.EMAIL || attr.name.toLowerCase() === COMPANY_EMPLOYEE_ID_KEY) &&
          employeeAttributeValues?.[matchingKey]
            ? employeeAttributeValues?.[matchingKey].toLowerCase()
            : employeeAttributeValues?.[matchingKey];

        if (value === undefined) return acc;

        const updatedAttributeValue = {
          id: attr.id,
          value,
        };

        return [...acc, updatedAttributeValue];
      },
      [] as { id: number; value: string }[],
    );

    return {
      employeeId: companyEmployeeId,
      enabled,
      languageId,
      attributes: importableAttributeValues,
      ...(ROLE_KEY in employee ? { role: employee[ROLE_KEY] } : {}),
    };
  });

  const { data: res } = await graphqlRequest<any>(ImportEmployeesDocument, {
    isUpdate,
    hasRoleUpdate,
    companyId: account.companyId,
    employees: importableEmployees,
  });
  const hasError = !res || !res.importEmployees?.isSuccess || res.error;

  if (hasError) {
    /* istanbul ignore start -- defensive: Apollo throws on null data, so this branch is unreachable */
    if (!res) {
      return rejectWithValue({
        title: l10n.t('employee.import.error.unableToImport'),
        message: l10n.t('error.somethingWentWrong'),
      });
    }
    /* istanbul ignore stop */

    if (!res?.importEmployees?.isSuccess && Array.isArray(res?.importEmployees?.conflicts)) {
      const errorMsg = res.importEmployees.conflicts.reduce((acc, conflictError) => {
        const { attributeId, attributeValue } = conflictError;
        const attribute = attributes.find((attr) => attr.id === Number(attributeId));
        const msg = attribute
          ? `${l10n.t('survey.duplicate')} ${attribute.name}: '${attributeValue}'`
          : `${l10n.t('survey.duplicate')} ${attributeId}: '${attributeValue}'`;

        // Separate each error message with a line break and a bullet point.
        return acc + `- ${msg}\n`;
      }, '');

      return rejectWithValue({
        title: l10n.t('employee.import.error.unableToImport'),
        message: errorMsg,
      });
    }

    return rejectWithValue(res?.error);
  }

  // Refresh the current employees page data if there were no errors.
  const {
    search,
    attributeValues: selectedAttributeValues,
    filterBy,
    orderBy,
    order,
    page,
    per,
  } = getState().ui.employees;

  dispatch(
    // @ts-ignore @TODO Epic WT-21600
    pageEmployees({
      attributeValues: selectedAttributeValues,
      filterBy,
      orderBy,
      // @ts-ignore @TODO Epic WT-21600
      order,
      page,
      per,
      search,
    }),
  );

  return {
    notification: {
      title: l10n.t('notifications.title.done'),
      message: l10n.t('employee.import.success'),
      status: notificationStatuses.success,
    },
  };
});

const genSampleUpdateEmployeeInput = createAction('employee/genSampleUpdateEmployeeInput', () => {
  const sampleInput = [
    {
      currentEmployeeId: 'tester@worktango.com',
      newEmployeeId: '123001',
    },
  ];

  const filename = 'Update-Employee-Ids-Sample';

  const employeesTab = {
    name: l10n.t('employee.upload.updateEmployeeIds'),
    titles: ['CurrentEmployeeId', 'NewEmployeeId'],
    rows: sampleInput.map((employeeIds) => [employeeIds.currentEmployeeId, employeeIds.newEmployeeId]),
  };

  // Will trigger browser download.
  excel.toFile(filename, [employeesTab]);

  return {
    payload: {
      filename,
    },
  };
});

const checkDuplication = (idPair, compareList) => {
  const { currentEmployeeId, newEmployeeId } = idPair;
  let duplicatedCurrentId;
  let duplicatedNewId;
  compareList.forEach((compareIdPair) => {
    if (currentEmployeeId === compareIdPair.currentEmployeeId) {
      duplicatedCurrentId = currentEmployeeId;
    }
    if (newEmployeeId === compareIdPair.newEmployeeId) {
      duplicatedNewId = newEmployeeId;
    }
  });
  return { duplicatedCurrentId, duplicatedNewId };
};

const validateUpdateEmployeeData = createAsyncThunk<
  { payload: { isValid: boolean; errorMsg?: string } } | unknown,
  { buffer: ArrayBuffer; companyId: number }
>('employee/validateUpdateEmployeeData', async ({ buffer, companyId }, { rejectWithValue }) => {
  const rawInputs = excel.employeeIdsToJson(buffer);
  const isValidDataFormat =
    Object.keys(rawInputs[0]).length === 2 && rawInputs[0].CurrentEmployeeId && rawInputs[0].NewEmployeeId;
  if (!isValidDataFormat) {
    return {
      isValid: false,
      errorMsg: '- Invalid input table format',
    };
  }
  const updateEmployeeIds = rawInputs.map((updateEmployeeIds) => ({
    currentEmployeeId: String(updateEmployeeIds.CurrentEmployeeId).toLowerCase(),
    newEmployeeId: String(updateEmployeeIds.NewEmployeeId).toLowerCase(),
  }));
  const { data: companyEmployeeIdsRes } = await graphqlRequest<any>(FetchCompanyEmployeeIdsDocument, { companyId });
  if (!companyEmployeeIdsRes?.employeeDetails?.results) {
    return rejectWithValue(`Unable to fetch company employee IDs for company ${companyId}`);
  }
  const companyEmployeeIdsResults = coalesce(companyEmployeeIdsRes?.employeeDetails?.results, []);
  const companyEmployeeIds = companyEmployeeIdsResults.map((employee) => employee.employeeId.toLowerCase());

  const dataErrors = {
    duplicatedCurrentIds: [] as string[],
    duplicatedNewIds: [] as string[],
    blankCurrentIds: [] as number[],
    blankNewIds: [] as number[],
    invalidCurrentIds: [] as string[],
    occupiedNewIds: [] as string[],
  };

  let existingIdFullSet = new Set(companyEmployeeIds);
  let existingIdSet = new Set(companyEmployeeIds);
  let rowNo = 2;
  updateEmployeeIds.forEach((updateEmployeeId) => {
    const remainingIds = updateEmployeeIds.slice(rowNo - 1);
    const currentId = updateEmployeeId.currentEmployeeId;
    const newId = updateEmployeeId.newEmployeeId;

    if (!currentId && !newId) {
      dataErrors.blankCurrentIds.push(rowNo);
      dataErrors.blankNewIds.push(rowNo);
      rowNo++;
      return;
    }

    if (!currentId) {
      dataErrors.blankCurrentIds.push(rowNo);
    } else if (!existingIdSet.has(currentId) && !dataErrors.invalidCurrentIds.includes(currentId)) {
      dataErrors.invalidCurrentIds.push(currentId);
    }
    if (!newId) {
      dataErrors.blankNewIds.push(rowNo);
    } else if (existingIdFullSet.has(newId) && !dataErrors.occupiedNewIds.includes(newId)) {
      dataErrors.occupiedNewIds.push(newId);
    }

    const { duplicatedCurrentId, duplicatedNewId } = checkDuplication(updateEmployeeId, remainingIds);
    if (duplicatedCurrentId && dataErrors.duplicatedCurrentIds.indexOf(duplicatedCurrentId) === -1) {
      dataErrors.duplicatedCurrentIds.push(duplicatedCurrentId);
    }
    if (duplicatedNewId && dataErrors.duplicatedNewIds.indexOf(duplicatedNewId) === -1) {
      dataErrors.duplicatedNewIds.push(duplicatedNewId);
    }

    rowNo++;
    if (currentId && !duplicatedCurrentId) existingIdSet.delete(currentId);
  });

  const isValid = Object.keys(dataErrors).reduce((validity, type) => {
    if (!validity) return validity;
    const hasErrors = dataErrors[type].length > 0;

    return !hasErrors;
  }, true);

  const errorMsg = isValid
    ? ''
    : Object.keys(dataErrors).reduce((accMsg, type) => {
        if (dataErrors[type].length === 0) return accMsg;

        const isBlankIdError = type === 'blankCurrentIds' || type === 'blankNewIds';
        const msgGroupHeader = isBlankIdError
          ? l10n.t(`employee.update-ids.error.${type}`) + ` (${l10n.t('employee.update-ids.error.row-no')})`
          : l10n.t(`employee.update-ids.error.${type}`);
        const msgGroup = dataErrors[type].reduce((acc, msg) => {
          return acc + `- ${msg}\n`;
        }, `${msgGroupHeader}:\n`);

        return accMsg + `${msgGroup}\n`;
      }, '');

  return { isValid, errorMsg };
});

const updateEmployeeIds = createAsyncThunk<
  Notification | unknown,
  { companyId: number; updatedEmployeeIds: { [key: string]: string }[] },
  { state: RootState; dispatch: AppDispatch }
>('employee/updateEmployeeIds', async ({ companyId, updatedEmployeeIds }, { getState, dispatch, rejectWithValue }) => {
  const validUpdatedIds = updatedEmployeeIds.map((updateEmployeeIds) => ({
    currentEmployeeId: String(updateEmployeeIds.CurrentEmployeeId).toLowerCase(),
    newEmployeeId: String(updateEmployeeIds.NewEmployeeId).toLowerCase(),
  }));
  try {
    const { data: res } = await graphqlRequest<any>(UpdateCompanyEmployeeIdsDocument, {
      companyId,
      updateEmployeeIdInput: validUpdatedIds,
    });

    if (res.updateCompanyEmployeeIds?.status === 'Failed') {
      const errorResult = res.updateCompanyEmployeeIds?.error;
      const errorMsg = `${l10n.t(errorResult.translationKey)}: ${errorResult.failedId}`;

      return rejectWithValue({
        title: l10n.t('employee.update-ids.error.unable-update'),
        message: errorMsg,
      });
    }
  } catch (error) {
    return rejectWithValue({
      title: l10n.t('employee.update-ids.error.unable-update'),
      message: l10n.t('error.somethingWentWrong'),
    });
  }

  // Refresh the current employees page data if there were no errors.
  const {
    search,
    attributeValues: selectedAttributeValues,
    filterBy,
    orderBy,
    order,
    page,
    per,
  } = getState().ui.employees;

  dispatch(
    // @ts-ignore @TODO Epic WT-21600
    pageEmployees({
      attributeValues: selectedAttributeValues,
      filterBy,
      orderBy,
      // @ts-ignore @TODO Epic WT-21600
      order,
      page,
      per,
      search,
    }),
  );

  return {
    notification: {
      title: l10n.t('notifications.title.done'),
      message: l10n.t('employee.update-ids.success'),
      status: notificationStatuses.success,
    },
  };
});

const validateUpdateIndividualEmployeeId = createAction(
  'employee/validateUpdateIndividualEmployeeId',
  ({ newEmployeeId, companyEmployeeIds }) => {
    const newId = String(newEmployeeId).toLowerCase();
    let isNewIdValid = false;

    const existingIdSet = new Set(companyEmployeeIds);
    if (!existingIdSet.has(newId)) {
      isNewIdValid = true;
    }

    const errorMsg = isNewIdValid ? '' : l10n.t('employee.update-ids.error.occupied-new-id');

    return {
      payload: {
        isValid: isNewIdValid,
        errorMsg,
      },
    };
  },
);

export const employeeActions = {
  fetchEmployee,
  fetchEmployees,
  generateSampleEmployeeImport,
  pageEmployees,
  modifyEmployee,
  createEmployee,
  removeEmployee,
  exportEmployees,
  importEmployees,
  validateEmployeesImportData,
  genSampleUpdateEmployeeInput,
  validateUpdateEmployeeData,
  updateEmployeeIds,
  validateUpdateIndividualEmployeeId,
};
